import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isNil } from 'ramda';

import { TRANSACTIONS_PAGE_SIZE } from '../../../pages/constants';
import TransactionPresenter from '../../../presenters/TransactionPresenter';
import TransactionCell from '../../../components/TransactionCell';
import { PaginatedList, Dialog } from '../../../components';
import appRoutes from '../../../routes/appRoutes';
import Row from '../../../components/Row';
import Column from '../../../components/Column';
import Checkbox from '../../../components/Checkbox';
import { multipleCashbackRestHooks } from '../../../store/Cashbacks/MultipleCashbackSlice';
import { transactionsRestHooks } from '../../../store/Transactions/TransactionsSlice';

import EditTransactionModal from './components/EditTransactionModal';
import IntegrationItemsFilter from './components/IntegrationItemsFilter';
import BankAccountsFilter from './components/BankAccountsFilter';
import StoresFilter from './components/StoresFilter';
import TabsSwitch from './components/TabsSwitch';

const TransactionsList = (props) => {
  const {
    transactions,
    relay,
    lists,
    fastCashId,
    bankAccountsInfo,
    searchParams,
    searchParams: {
      bankAccountIdEq,
      stateEq,
      storeIdEq,
      integrationItemIdEq,
      nameCont,
      byMerchantNames,
      nameNotIContAll,
      order,
    },
    onSearchParamsChange,
    integrationItemsInfo,
    stores,
  } = props;

  const [stateNameCont, setNameCont] = useState(nameCont);
  const [stateByMerchantNames, setByMerchantNames] = useState(byMerchantNames);
  const [stateOrder, setOrder] = useState(order);
  const [stateNameNotIContAll, setNameNotIContAll] = useState(nameNotIContAll);
  const [cashbacksSelected, setCashbacksSelected] = useState([]);
  const [isCashbackDialogOpen, setIsCashbackDialogOpen] = useState(false);
  const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [storesUpdated, setStoresUpdated] = useState(false);
  const [ignoreMembership, setIgnoreMembership] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation();
  const history = useHistory();

  const handleLoadMore = (completion) => {
    relay.loadMore(TRANSACTIONS_PAGE_SIZE, completion);
  };

  const {
    plaidTransactions: {
      pageInfo: { hasNextPage },
    },
  } = lists;

  const {
    createMultipleCashback,
    multipleCashback: { errors: multipleCashbackErrors },
  } = multipleCashbackRestHooks.use();

  const { updateTransaction } = transactionsRestHooks.use();

  useEffect(() => {
    const invalidStore = multipleCashbackErrors?.find(
      (item) => item.invalid_store
    );
    const invalidMembership = multipleCashbackErrors?.find(
      (item) => item.invalid_membership
    );
    const fastcashDisabled = multipleCashbackErrors?.find(
      (item) => item.fastcash_disabled
    );
    if (isNil(multipleCashbackErrors) || cashbacksSelected.length == 0) return;
    setIsLoading(false);
    if ((errors?.invalid_store || errors?.is_missing_store) && invalidStore){
      return (
        setErrors({
          is_missing_store: invalidStore.invalid_store,
          transactions: invalidStore.transactions,
        }),
        handleWarningDialogOpen()
      );
    }
    if (invalidStore) return setErrors(invalidStore), handleWarningDialogOpen();
    if (fastcashDisabled)
      return setErrors(fastcashDisabled), handleWarningDialogOpen();
    if (invalidMembership)
      return setErrors(invalidMembership), handleCashbackDialogOpen();
    setErrors(multipleCashbackErrors);
    return () => {
      setErrors({});
      setCashbacksSelected([]);
    };
  }, [multipleCashbackErrors]);

  const handleCashbackDialogOpen = () => setIsCashbackDialogOpen(true);
  const handleCashbackDialogClose = (isFromCreate) => {
    errors.invalid_membership && !isFromCreate && window.location.reload();
    setIsCashbackDialogOpen(false);
    setIgnoreMembership([]);
    !isFromCreate && setErrors({});
    storesUpdated && !isFromCreate && window.location.reload();
  };
  const handleWarningDialogOpen = () => setIsWarningDialogOpen(true);
  const handleWarningDialogClose = (openEditModal) => {
    setIsWarningDialogOpen(false);
    setIgnoreMembership([]);
    openEditModal && handleEditModalOpen(true);
  };
  const handleEditModalOpen = () => setIsEditModalOpen(true);
  const handleEditModalClose = (isFulfilled) => {
    setIsEditModalOpen(false);
    storesUpdated && !isFulfilled && window.location.reload();
  };

  const warning = () => {
    if (errors.is_missing_store)
      return t('transaction.dialogs.warnings.missingStore');
    if (errors.fastcash_disabled)
      return t('transaction.dialogs.warnings.invalidStore');
    if (errors.invalid_store)
      return t('transaction.dialogs.warnings.updateStore');
    return '';
  };

  const defaultCreateCashbackWarning = t(
    'transaction.dialogs.warnings.createCashback'
  );
  const ignoreMembershipWarning = t(
    'transaction.dialogs.warnings.forceCreateCashbackList'
  );
  const createCashbackWarning = errors.invalid_membership
    ? ignoreMembershipWarning
    : defaultCreateCashbackWarning;

  const handleStateChange = (newState) => {
    onSearchParamsChange({ ...searchParams, stateEq: newState });
  };

  const handleBankAccountIdChange = ({ target: { value } }) => {
    onSearchParamsChange({ ...searchParams, bankAccountIdEq: value });
  };

  const handleBonusButton = () => {
    const path = appRoutes.newBonusPath(searchParams.byAuth0Id);
    history.push(path);
  };

  const handleCreateMultiCashback = async () => {
    handleCashbackDialogClose(true);
    setStoresUpdated(false);
    setIsLoading(true);
    if (errors.invalid_membership) {
      await cashbacksSelected.map((item) => {
        if (ignoreMembership.includes(Number(item.plaidTransactionId)))
          item.ignoreMembership = true;
      });
    }
    const cashbacks = await cashbacksSelected.filter(
      (item) => item.ignoreMembership
    );
    await createMultipleCashback({
      plaidTransactions: cashbacks.length > 0 ? cashbacks : cashbacksSelected,
    });
    setIsLoading(false);
    setCashbacksSelected([]);
    window.location.reload();
  };

  const handleTransactionUpdate = async (values, transaction, isLast) => {
    await updateTransaction(transaction.id, values);
    setStoresUpdated(true);
    if (isLast) {
      handleEditModalClose(true);
      return handleCashbackDialogOpen();
    }
  };

  const handleAddCashback = (transaction) => {
    const index = cashbacksSelected.findIndex(
      (item) => item.plaidTransactionId === transaction.plaidTransactionId
    );
    if (index === -1) {
      setCashbacksSelected([...cashbacksSelected, transaction]);
    } else {
      const newArray = cashbacksSelected;
      newArray.splice(index, 1);
      setCashbacksSelected([...newArray]);
    }
  };

  const handleStoreIdChange = ({ target: { value } }) => {
    onSearchParamsChange({ ...searchParams, storeIdEq: value });
  };

  const handleIntegrationItemIdChange = ({ target: { value } }) => {
    const num = Number(value);
    onSearchParamsChange({ ...searchParams, integrationItemIdEq: num });
  };

  const handleNameCont = ({ target }) => {
    const { id, value } = target;
    setNameCont(value);
    handleInputChange(id, value);
  };

  const handleByMerchantNames = ({ target }) => {
    const { id, value } = target;
    setByMerchantNames(value);
    handleInputChange(id, value);
  };

  const handleChangeOrder = ({ target }) => {
    const { id, value } = target;
    setOrder(value);
    onSearchParamsChange({ ...searchParams, order: value });
  };

  const handleNameNotIContAll = ({ target }) => {
    const { id, value } = target;
    setNameNotIContAll(value);
    handleInputChange(id, value);
  };

  const handleInputChange = (id, value) => {
    setTimeout(() => {
      onSearchParamsChange({ ...searchParams, [id]: value });
    }, 2000);
  };

  const bankAccountsFilter = () => {
    if (integrationItemIdEq) {
      const bankSelected = integrationItemsInfo?.filter(
        (integrationItem) => integrationItem.id == integrationItemIdEq
      );
      const bankAccountsFilter = bankAccountsInfo?.filter(
        (bankAccount) =>
          bankAccount.institutionName === bankSelected[0]?.institutionName
      );
      return bankAccountsFilter;
    }
    return bankAccountsInfo;
  };

  const isChecked = (id) => {
    const index = ignoreMembership?.findIndex((item) => item === id);
    if (index !== -1) {
      return true;
    } else {
      return false;
    }
  };

  const handleCheckIgnoreMembership = (value) => {
    const index = ignoreMembership?.findIndex((item) => item === value.id);
    if (index === -1) {
      setIgnoreMembership([...ignoreMembership, value.id]);
    } else {
      const newArray = ignoreMembership;
      newArray.splice(index, 1);
      setIgnoreMembership([...newArray]);
    }
  };

  return (
    <div className="transactions">
      <div className="header">
        <h3>Transactions list</h3>
        <div className="actions" style={{ marginBottom: 20 }}>
          <button className="button" onClick={handleBonusButton}>
            Bonus
          </button>
        </div>
      </div>
      {fastCashId && (
        <>
          <IntegrationItemsFilter
            integrationItemsId={integrationItemIdEq}
            integrationItemsInfo={integrationItemsInfo}
            onChange={handleIntegrationItemIdChange}
          />
          <BankAccountsFilter
            bankAccountId={bankAccountIdEq}
            bankAccountsInfo={bankAccountsFilter()}
            onChange={handleBankAccountIdChange}
          />
          <StoresFilter
            storeId={storeIdEq}
            storesInfo={stores}
            onChange={handleStoreIdChange}
          />
          <Row className="transactions-filters">
            <input
              type="text"
              id="nameCont"
              placeholder="Transaction Name Contains"
              value={stateNameCont}
              onChange={handleNameCont}
              className="input-underlined input-row"
            />
            <input
              type="text"
              id="nameNotIContAll"
              placeholder="Transaction Name Excludes"
              value={stateNameNotIContAll}
              onChange={handleNameNotIContAll}
              className="input-underlined input-row"
            />
            <input
              type="text"
              id="byMerchantNames"
              placeholder="Merchant Name Contains"
              value={stateByMerchantNames}
              onChange={handleByMerchantNames}
              className="input-underlined input-row"
            />
            <select
              id="order"
              defaultValue={stateOrder}
              onChange={handleChangeOrder}
            >
              <option value=""> -- select order -- </option>
              <option value={'ASC'} key={'ASC'}>
                Ascending
              </option>
              <option value={'DESC'} key={'DESC'}>
                Descending
              </option>
            </select>
          </Row>
        </>
      )}
      <div className="multi-cashback-buttons" style={{ marginBottom: 20 }}>
        {cashbacksSelected.length > 0 && (
          <a
            onClick={() => setCashbacksSelected([])}
            className="redText selected-text underline"
          >
            UNSELECT ALL
          </a>
        )}
        <p className="blueText selected-text">
          ({cashbacksSelected.length} OF 10 SELECTED)
        </p>
        {isLoading ? (
          <div className="lds-dual-ring-small"></div>
        ) : (
          <button
            className="button"
            onClick={handleCashbackDialogOpen}
            disabled={cashbacksSelected.length == 0}
          >
            Create cashback
          </button>
        )}
      </div>
      <TabsSwitch currentState={stateEq} onStateChange={handleStateChange} />
      <PaginatedList canLoadMore={hasNextPage} onLoadMore={handleLoadMore}>
        <table className="tables">
          <thead>
            <tr>
              <th></th>
              <th>Date</th>
              <th>Transaction name</th>
              <th>Store name</th>
              <th>Location</th>
              <th>Merchant name</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Cashback rate</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((transaction, index, transactions) => {
              const previousTransaction = transactions[index - 1];
              return (
                <TransactionCell
                  key={transaction.id}
                  transaction={transaction}
                  previousTransaction={previousTransaction}
                  cashbacksSelected={cashbacksSelected}
                  handleCheckbox={handleAddCashback}
                />
              );
            })}
          </tbody>
        </table>
      </PaginatedList>
      <Dialog
        isOpen={isCashbackDialogOpen}
        message={createCashbackWarning}
        onConfirm={handleCreateMultiCashback}
        onCancel={() => handleCashbackDialogClose(false)}
        onConfirmDisabled={
          errors.invalid_membership && ignoreMembership.length == 0
        }
      >
        {errors.invalid_membership &&
          errors?.transactions?.map((transaction, i) => {
            return (
              <Checkbox
                id={transaction.id}
                onChange={() => handleCheckIgnoreMembership(transaction)}
                checked={isChecked(transaction.id)}
                labelText={`  ${TransactionPresenter.dateFormat(
                  transaction
                )} - ${TransactionPresenter.name(
                  transaction
                )} - ${TransactionPresenter.amountReadable(transaction)}`}
                className="dialog-black-text-small"
              />
            );
          })}
      </Dialog>
      <Dialog
        className="warning"
        isOpen={isWarningDialogOpen}
        message={warning()}
        onConfirm={() => handleWarningDialogClose(true)}
        onCancel={() => handleWarningDialogClose(false)}
      />
      <EditTransactionModal
        transactions={errors?.transactions ? errors.transactions : []}
        isOpen={isEditModalOpen}
        onClose={() => handleEditModalClose(false)}
        onTransactionUpdate={handleTransactionUpdate}
        stores={stores}
      />
    </div>
  );
};

TransactionsList.propTypes = {
  transactions: PropTypes.arrayOf(TransactionPresenter.shape()).isRequired,
  relay: PropTypes.shape().isRequired,
  lists: PropTypes.shape().isRequired,
  searchParams: PropTypes.shape({
    bankAccountIdEq: PropTypes.string,
    stateEq: PropTypes.string,
  }).isRequired,
  fastCashId: PropTypes.string,
  bankAccountsInfo: PropTypes.arrayOf(PropTypes.shape()),
  onSearchParamsChange: PropTypes.func.isRequired,
};

TransactionsList.defaultProps = {
  fastCashId: null,
  bankAccountsInfo: [],
};

export default TransactionsList;
